// Unpublished Work © 2026 Deere & Company.
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {GRID_REORDER_COL_DEF, MultiSelect, Stack, Pill, EllipsisOverflow, Button, Tooltip} from '@deere/isg.component-library';
import {IconAdd} from '@deere/icons';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {isAggregationFooterRow} from 'Ui/components/common/data-table/data-grid-utils';
import {sortWorkItemsBySeq} from './utils/work-item-utils';
import {getAssignedOperators, getAssignedJobs, addNewItemToMultiselectValue} from './utils/assign-helpers';

function getSelectedTitles(selectedIds, multiSelectItems, isGrouped, placeholder) {
    let items = [];

    if (!selectedIds?.length) {
        return [placeholder];
    }
    if (isGrouped) {
        multiSelectItems.forEach((group) => {
            group.items.forEach((item) => {
                items.push(item);
            });
        });
    } else {
        items = multiSelectItems;
    }
    const ids = new Set(selectedIds);
    const titles = [];

    items.forEach((item) => {
        if (ids.has(item.id)) {
            titles.push(item.title);
        }
    });

    return titles;
}

function pinSelectedItemsToTop(multiSelectItems, selectedIds, isGrouped) {
    const selectedIdSet = new Set(selectedIds);

    if (!isGrouped) {
        const pinned = [];
        const rest = [];

        multiSelectItems.forEach((item) => {
            if (selectedIdSet.has(item.id)) {
                pinned.push(item);
            } else {
                rest.push(item);
            }
        });
        return [...pinned, ...rest];
    }

    return multiSelectItems.map((group) => {
        const pinned = [];
        const rest = [];

        group.items.forEach((item) => {
            selectedIdSet.has(item.id) ? pinned.push(item) : rest.push(item);
        });
        return {
            ...group,
            items: [...pinned, ...rest]
        };
    });
}

function MultiSelectWithNamesTooltip({
    selectedTitles, isMobile, children
}) {
    const containerRef = React.useRef(null);
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const [hovering, setHovering] = React.useState(false);

    React.useLayoutEffect(() => {
        const node = containerRef.current;
        const removeDefaultTooltips = () => {
            node.querySelectorAll('[title]').forEach((el) => el.removeAttribute('title'));
        };

        removeDefaultTooltips();
        const observer = new MutationObserver(removeDefaultTooltips);

        observer.observe(node, {
            attributes: true,
            subtree: true,
            attributeFilter: ['title']
        });
        return () => observer.disconnect();
    }, []);

    // Skip the tooltip on mobile: touch devices emulate mouseenter on tap but never fire mouseleave, so the tooltip would get stuck open after the dropdown closes.
    if (isMobile) {
        return (
            <div
                ref={containerRef}
                style={{
                    width: '100%'
                }}
            >
                {children({
                    open: dropdownOpen,
                    onChangeOpen: setDropdownOpen
                })}
            </div>
        );
    }

    // Only show a tooltip when the user is hovering the closed dropdown button (not while the dropdown menu is open).
    const tooltipTitle = selectedTitles.join(', ');
    const tooltipOpen = Boolean(tooltipTitle) && hovering && !dropdownOpen;

    return (
        <Tooltip
            disableFocusListener
            disableHoverListener
            disableTouchListener
            open={tooltipOpen}
            placement='bottom'
            title={tooltipTitle}
        >
            <div
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                ref={containerRef}
                style={{
                    width: '100%'
                }}
            >
                {children({
                    open: dropdownOpen,
                    onChangeOpen: setDropdownOpen
                })}
            </div>
        </Tooltip>
    );
}

MultiSelectWithNamesTooltip.propTypes = {
    children: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    selectedTitles: PropTypes.arrayOf(PropTypes.string)
};

function OperatorMultiSelect({
    params, multiSelectItems, multiSelectValue, setMultiSelectValue, translations, openAddAssignmentsDialog, isMobile
}) {
    const selectedIds = multiSelectValue[params.row.id];
    const pinnedGroups = React.useMemo(() => pinSelectedItemsToTop(multiSelectItems, selectedIds, true), [multiSelectItems, selectedIds]);
    const selectedTitles = getSelectedTitles(selectedIds, multiSelectItems, true, translations.ONLINK_SELECT_OPERATORS);

    const addOperatorFooterAction =
        (<Button
            onClick={() => openAddAssignmentsDialog({
                addUser: true,
                handleAddedItems: (ids) => addNewItemToMultiselectValue(setMultiSelectValue, params.row.id, ids)
            })}
            startIcon={<IconAdd/>}
            variant='borderless'
        >
            {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': translations.OPERATORS
            })}
        </Button>);

    return (
        <MultiSelectWithNamesTooltip
            isMobile={isMobile}
            selectedTitles={selectedTitles}
        >
            {({
                open, onChangeOpen
            }) => (
                <MultiSelect
                    disableSorting={true}
                    footerAction={addOperatorFooterAction}
                    groups={pinnedGroups}
                    items={[]}
                    labels={{
                        placeholder: translations.ONLINK_SELECT_OPERATORS,
                        multiSelect: translations.ONLINK_OPERATORS_SELECTED,
                        emptyList: translations.ONLINK_WORKBOARD_NO_RESULTS,
                        noItemMatch: translations.NO_RESULTS_FOUND,
                        allSelect: translations.ALL_SELECTED
                    }}
                    onChange={(newSelectedIds) => addNewItemToMultiselectValue(setMultiSelectValue, params.row.id, newSelectedIds, true)}
                    onChangeOpen={onChangeOpen}
                    open={open}
                    selectedIds={selectedIds}
                />
            )}
        </MultiSelectWithNamesTooltip>
    );
}

OperatorMultiSelect.propTypes = {
    isMobile: PropTypes.bool,
    multiSelectItems: PropTypes.array,
    multiSelectValue: PropTypes.object,
    openAddAssignmentsDialog: PropTypes.func,
    params: PropTypes.object,
    setMultiSelectValue: PropTypes.func,
    translations: PropTypes.object
};

function renderReorderCell(params, translations) {
    if (isAggregationFooterRow(params)) {
        return translations.TOTALS;
    }
    return GRID_REORDER_COL_DEF.renderCell(params);
}

function renderNoteFooter(workItems, translations) {
    let count = 0;

    Object.entries(workItems).map(([, workItem]) => {
        if (workItem.note) {
            count += 1;
        }
    });
    return `${count} ${translations.NOTES}`;
}

function renderNoteCell(workItems, filterField, params, openRowDetails) {
    const entries = Object.entries(workItems).filter(([, item]) => item[filterField] === params.row.id).map(([, workItem]) => workItem);
    const selectedWorkItems = sortWorkItemsBySeq(entries);
    const withNotes = selectedWorkItems.filter(
        (item) => item.note?.trim().length > 0
    );
    const additionalCount = Math.max(withNotes.length - 1, 0);

    const notes = withNotes[0]?.note;

    if (!notes) {
        return '---';
    }

    return (
        <Stack
            alignItems='center'
            direction='row'
            spacing={1}
            sx={{
                width: '100%',
                minWidth: 0
            }}
        >

            <EllipsisOverflow
                slotProps={{
                    typography: {
                        component: 'p'
                    }
                }}
                style={{
                    width: '80%'
                }}
                tooltipPlacement='bottom'
            >
                {notes}
            </EllipsisOverflow>

            {additionalCount > 0 && (
                <Pill
                    lineHeight={'normal'}
                    onClick={(e) => {
                        e.preventDefault();
                        openRowDetails(params.row);
                    }}
                >
                    {`+${additionalCount}`}
                </Pill>
            )}

        </Stack>
    );
}

function renderEquipmentFooter(workItems, translations) {
    let count = 0;

    Object.entries(workItems).map(([, workItem]) => {
        if (workItem.equipment) {
            count += workItem.equipment.length;
        }
    });
    return `${count} ${translations.ASSIGNED_EQUIPMENT}`;
}

function renderEquipmentCell(workItems, filterField, params) {
    const entries = Object.entries(workItems);
    const selectedWorkItems = entries.filter(([, item]) => item[filterField] === params.row.id).map(([, workItem]) => workItem);
    const equipment = selectedWorkItems.flatMap((wItem) => wItem.equipment);
    const assignedEquipment = equipment.flatMap((eq) => eq.equipmentName).join(', ');

    if (!assignedEquipment) {
        return '---';
    }

    return (
        <EllipsisOverflow
            slotProps={{
                typography: {
                    component: 'p'
                }
            }}
            style={{
                width: '100%'
            }}
            tooltipPlacement='bottom'
        >
            {assignedEquipment}
        </EllipsisOverflow>
    );
}

const getJobColumns = (isMobile, translations, workItems, multiSelectItems, multiSelectValue, setMultiSelectValue, totalOperatorCount, openRowDetails, openAddAssignmentsDialog) => [
    !isMobile && {
        ...GRID_REORDER_COL_DEF,
        maxWidth: 150,
        renderCell: (params) => renderReorderCell(params, translations)
    },
    {
        headerName: translations.JOBS,
        field: 'jobName',
        minWidth: 150,
        flex: 1
    },
    {
        headerName: translations.OPERATORS,
        field: 'operators',
        minWidth: 150,
        flex: 1,
        cellClassName: 'tour-step-1',
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return `${getAssignedOperators(multiSelectValue)} ${translations.ASSIGNED_OPERATORS}`;
            }
            return (
                <OperatorMultiSelect
                    isMobile={isMobile}
                    multiSelectItems={multiSelectItems}
                    multiSelectValue={multiSelectValue}
                    openAddAssignmentsDialog={openAddAssignmentsDialog}
                    params={params}
                    setMultiSelectValue={setMultiSelectValue}
                    translations={translations}
                />
            );
        },
        display: 'flex'
    },
    !isMobile && {
        headerName: translations.ONLINK_OPERATOR_COUNT,
        field: 'operatorCount',
        minWidth: 150,
        flex: 1,
        type: 'number',
        cellClassName: 'tour-step-2',
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return `${Math.max(totalOperatorCount - getAssignedOperators(multiSelectValue), 0)} ${translations.UNASSIGNED_OPERATORS}`;
            }
            const currentCount = multiSelectValue[params.row.id]?.length;

            return (
                <>
                    {replaceTranslationNames(translations.OF, {
                        '0': currentCount ?? 0,
                        '1': params.row.operatorCount
                    })}
                </>
            );
        }
    },
    !isMobile && {
        headerName: translations.NOTE,
        field: 'note',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return renderNoteFooter(workItems, translations);
            }
            return renderNoteCell(workItems, 'jobId', params, openRowDetails);
        }
    },
    !isMobile && {
        headerName: translations.EQUIPMENT,
        field: 'equipment',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return renderEquipmentFooter(workItems, translations);
            }
            return renderEquipmentCell(workItems, 'jobId', params);
        }
    }
].filter(Boolean);

const getUserColumns = (isMobile, translations, workItems, multiSelectItems, multiSelectValue, setMultiSelectValue, openRowDetails, openAddAssignmentsDialog) => [
    !isMobile && {
        ...GRID_REORDER_COL_DEF,
        maxWidth: 150,
        renderCell: (params) => renderReorderCell(params, translations)
    },
    {
        headerName: translations.OPERATORS,
        field: 'operatorName',
        minWidth: 150,
        flex: 1
    },
    {
        headerName: translations.JOBS,
        field: 'jobs',
        minWidth: 150,
        flex: 1,
        cellClassName: 'tour-step-1',
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return `${getAssignedJobs(multiSelectValue)} ${translations.ASSIGNED_JOBS}`;
            }
            const addJobFooterAction =
                (<Button
                    onClick={() => openAddAssignmentsDialog({
                        addUser: false,
                        handleAddedItems: (ids) => addNewItemToMultiselectValue(setMultiSelectValue, params.row.id, ids)
                    })}
                    startIcon={<IconAdd/>}
                    variant='borderless'
                >
                    {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                        '0': translations.JOBS
                    })}
                </Button>);
            const selectedIds = multiSelectValue[params.row.id];
            const selectedTitles = getSelectedTitles(selectedIds, multiSelectItems, false, translations.ONLINK_SELECT_JOBS);
            const pinnedItems = React.useMemo(() => pinSelectedItemsToTop(multiSelectItems, selectedIds, false), [multiSelectItems, selectedIds]);

            return (
                <MultiSelectWithNamesTooltip
                    isMobile={isMobile}
                    selectedTitles={selectedTitles}
                >
                    {({
                        open, onChangeOpen
                    }) => (
                        <MultiSelect
                            disableSorting={true}
                            footerAction={addJobFooterAction}
                            items={pinnedItems}
                            labels={{
                                placeholder: translations.ONLINK_SELECT_JOBS,
                                multiSelect: translations.ONLINK_JOBS_SELECTED,
                                emptyList: translations.ONLINK_WORKBOARD_NO_RESULTS,
                                noItemMatch: translations.NO_RESULTS_FOUND,
                                allSelect: translations.ALL_SELECTED
                            }}
                            onChange={(newSelectedIds) => addNewItemToMultiselectValue(setMultiSelectValue, params.row.id, newSelectedIds, true)}
                            onChangeOpen={onChangeOpen}
                            open={open}
                            selectedIds={selectedIds}
                        />
                    )}
                </MultiSelectWithNamesTooltip>
            );
        },
        display: 'flex'
    },
    !isMobile && {
        headerName: translations.NOTE,
        field: 'note',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return renderNoteFooter(workItems, translations);
            }
            return renderNoteCell(workItems, 'operatorId', params, openRowDetails);
        }
    },
    !isMobile && {
        headerName: translations.EQUIPMENT,
        field: 'equipment',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
            if (isAggregationFooterRow(params)) {
                return renderEquipmentFooter(workItems, translations);
            }
            return renderEquipmentCell(workItems, 'operatorId', params);
        }
    }
].filter(Boolean);

export function getColumns(isMobile, translations, workItems, multiSelectItems, multiSelectValue, setMultiSelectValue, totalOperatorCount, openRowDetails, groupBy, openAddAssignmentsDialog) {
    if (groupBy === JOB) {
        return getJobColumns(isMobile, translations, workItems, multiSelectItems, multiSelectValue, setMultiSelectValue, totalOperatorCount, openRowDetails, openAddAssignmentsDialog);
    }
    return getUserColumns(isMobile, translations, workItems, multiSelectItems, multiSelectValue, setMultiSelectValue, openRowDetails, openAddAssignmentsDialog);
}
